import moment from 'moment';
import { DEFAULT_BOOKING_OPTIONS, salutationOptions } from '../config/customConfig';
import { useConfiguration } from '../context/configurationContext';
import { USER_TYPE } from '../helpers/enums';
import { cleanVimeoUrl, getDefaultLocale, isArrayLength } from './genericHelpers';
import { SENDGRID_CONTRACT_LIST } from './enums';

/**
 * Generates an array of image URLs using the base URL and an array of image objects.
 *
 * @param {Array} images - An array of image objects.
 * @param {string} url - The base URL for the images.
 * @returns {Array} An array of image URLs.
 * @throws {TypeError} If the `images` parameter is not an array.
 */
export const getImagesLink = (images, url) => {
  // Generate an array of image URLs using the base URL and the image object's URL attribute.
  const imageUrls =
    isArrayLength(images) &&
    images.map(image => {
      const imageUrl = image?.attributes?.url;
      return imageUrl;
    });

  // Return the array of image URLs.
  return imageUrls;
};

const BLOG_PUBLISH_DATE_FIELDS = ['publishedAt', 'publishDate', 'publishedDate'];

export const getBlogPublishDate = blog => {
  const attributes = blog?.attributes || {};
  const publishDateField = BLOG_PUBLISH_DATE_FIELDS.find(field => {
    const value = attributes[field];
    return typeof value === 'string' && value.trim() !== '';
  });

  return publishDateField ? attributes[publishDateField] : '';
};

export const getBlogPublishTimestamp = blog => {
  const publishDate = getBlogPublishDate(blog);
  const parsedDate = publishDate ? moment.utc(publishDate) : null;

  return parsedDate?.isValid() ? parsedDate.valueOf() : 0;
};

export const formatBlogPublishDate = (blog, format = 'Do, MMMM YY') => {
  const publishDate = getBlogPublishDate(blog);
  const parsedDate = publishDate ? moment.utc(publishDate) : null;

  return parsedDate?.isValid() ? parsedDate.format(format) : '';
};

/**
 * Extracts relevant data from a blog post object.
 *
 * @param {Object} blog - The blog post object.
 * @returns {Object} An object containing the blog post title, description, and image URL.
 * @throws {Error} If the `blog` parameter is not a valid object.
 */
export const getBlogData = blog => {
  // Get the base URL from the configuration.
  const config = useConfiguration();
  const baseUrl = config?.strapi?.baseURL || '';

  // Get the blog images and generate image URLs using the base URL.
  const blogImages = blog?.attributes?.images?.data || [];
  const imagesLink = blogImages?.length && getImagesLink(blogImages, baseUrl);

  // Get the blog title and description.
  const title = blog?.attributes?.title || '';
  const description = blog?.attributes?.description || '';
  const tag = blog?.attributes?.tagGreek || blog?.attributes?.tagEnglish || '';
  const createdAt = blog?.attributes?.createdAt || '';
  const publishedAt = blog?.attributes?.publishedAt || '';
  const publishDate = getBlogPublishDate(blog);
  const slug = blog?.attributes?.slug || '';

  // Return an object containing the blog title, description, and the first image URL (if available).
  return {
    title,
    description,
    tag,
    createdAt,
    publishedAt,
    publishDate,
    slug,
    image: imagesLink && imagesLink[0],
  };
};

export const isIndividualUser = user =>
  user?.attributes?.profile?.protectedData?.userType === USER_TYPE.INDIVIDUAL ||
  user?.attributes?.profile?.protectedData?.type === USER_TYPE.INDIVIDUAL;

export const isDoctorUser = user =>
  user?.attributes?.profile?.protectedData?.userType === USER_TYPE.DOCTOR ||
  user?.attributes?.profile?.protectedData?.type === USER_TYPE.DOCTOR;

export const getTermsTimestamp = user => user?.attributes?.profile?.protectedData?.termsVersion;

export const isUserCreatedWithinDays = (user, days = 7, now = moment()) => {
  const createdAt = user?.attributes?.createdAt;
  if (!createdAt) {
    return false;
  }

  const createdAtMoment = moment(createdAt);
  if (!createdAtMoment.isValid()) {
    return false;
  }

  return createdAtMoment.isAfter(moment(now).subtract(days, 'days'));
};

export const getFullName = user => {
  return (
    user?.id && user?.attributes?.profile?.firstName + ' ' + user?.attributes?.profile?.lastName
  );
};

export const getUserGender = user => {
  const salutation = user?.id && user?.attributes?.profile?.protectedData?.salutation;
  const gender = salutationOptions.find(s => s.key === salutation);
  return gender?.type;
};

export const getFirstName = user => {
  return user?.id && user?.attributes?.profile?.firstName;
};

export const getLastName = user => {
  return user?.id && user?.attributes?.profile?.lastName;
};

export const getFirstAndLastName = (listing, user) => {
  const currentLocaleFromStorage = getDefaultLocale();

  const title =
    currentLocaleFromStorage === 'en'
      ? listing?.attributes?.publicData?.title_en
      : listing?.attributes?.title;
  const listingName = title
    ? { firstName: title?.split(' ')[0], lastName: title?.split(' ')[1] }
    : null;

  return title ? listingName : { firstName: getFirstName(user), lastName: getLastName(user) };
};

export const getEmail = user => {
  return user?.id && user?.attributes?.email;
};
export const getSpeciality = user => {
  return user?.id && user?.attributes?.profile?.protectedData?.speciality;
};

export const isUsersStripeConnected = user => {
  return user?.id && user?.attributes?.profile?.publicData?.isStripeConnected;
};

export const isClientUser = user => {
  const email = user?.attributes?.email || user?.email;
  return email === 'client@vivianlab.com';
};

export const canAccessDashboard = user => {
  const allowedEmails = [
    'belei.tiberiu0425@gmail.com',
    'rebekka@vivianlab.com',
    'getvivianlab@gmail.com',
    'george.bakalis@vivianlab.com',
  ];

  return (
    user?.id &&
    (process.env.REACT_APP_ENV === 'development' || allowedEmails.includes(user?.attributes?.email))
  );
};

export const getListingImage = imgArr => {
  const firstImage = isArrayLength(imgArr) ? imgArr[0] : null;
  const image =
    firstImage?.attributes?.variants && firstImage?.attributes?.variants['listing-card']?.url;
  return image;
};

export const getSavedEventsData = listing => {
  return (listing?.id && listing?.attributes?.privateData?.eventsExtendedData) || [];
};

export const isInstantBooking = listing => {
  return listing?.id && listing?.attributes?.publicData?.instantBooking == 'yes';
};

export const getDisplayName = user => {
  return user?.id && user?.attributes?.profile?.displayName;
};

export const getTransactionServiceDetails = t => {
  return t?.id && t?.attributes?.protectedData?.service;
};
export const isTransactionWithCC = t => {
  return t?.attributes?.metadata?.isUnpaid === false || t?.attributes?.metadata?.isUnpaid === true;
};
export const getLastTransition = t => {
  return t?.id && t?.attributes?.lastTransition;
};

export const getServices = listing => {
  const currentLocaleFromStorage = getDefaultLocale();
  const services =
    currentLocaleFromStorage === 'en'
      ? listing?.attributes?.publicData?.en?.services
      : listing?.attributes?.publicData?.services;
  return (listing?.id && services) || [];
};

export const getCityCountry = listing => {
  const publicData = listing?.attributes?.publicData;
  return { country: publicData?.country, city: publicData?.city };
};

export const getGoogleAuthTokens = user => {
  return user?.id && user?.attributes?.profile?.protectedData?.googleAuthTokens;
};

export const isDoctorApproved = user => {
  return user?.id && user?.attributes?.profile?.protectedData?.approvalEmailSent;
};

export const getUserPreferredLang = user => {
  return user?.id && user?.attributes?.profile?.protectedData?.preferredLang;
};
export const isUserEmailVerified = user => {
  return user?.id && user?.attributes?.emailVerified;
};

export const getMeetingLink = tx => {
  return tx?.id && tx?.attributes?.metadata?.meetingLink;
};

export const getMeetingStart = tx => {
  return tx?.id && tx?.booking?.attributes?.start;
};

export const getMeetingEnd = tx => {
  return tx?.id && tx?.booking?.attributes?.end;
};

const isNonEmptyString = val =>
  val !== undefined && val !== 'undefined' && typeof val === 'string' && val.trim() !== '';

const getProjectedProtectedData = user =>
  user?.attributes?.profile?.protectedData ||
  user?.attributes?.protectedData ||
  user?.profile?.protectedData ||
  user?.protectedData ||
  {};

const getProtectedProfileData = user => getProjectedProtectedData(user);

const getPrivateProfileData = user => user?.attributes?.profile?.privateData || {};

const AI_ENTITLEMENT_DIRECT_KEYS = [
  'aiAccess',
  'aiEnabled',
  'aiConsultAccess',
  'aiCoachAccess',
  'askVivianAccess',
  'aiBoxAccess',
  'ai_box',
];

const AI_ENTITLEMENT_NESTED_KEYS = ['entitlements', 'features', 'featureFlags', 'permissions'];

const AI_ENTITLEMENT_TOKENS = new Set([
  'ai',
  'ai_access',
  'ai_consult',
  'ai_consult_access',
  'ai_coach',
  'ai_coach_access',
  'ask_vivian',
  'askvivian',
  'ai_box',
]);

const normalizeEntitlementToken = value =>
  String(value || '')
    .trim()
    .toLowerCase();

const hasAiEntitlementToken = value => AI_ENTITLEMENT_TOKENS.has(normalizeEntitlementToken(value));

const readAiEntitlementFromContainer = container => {
  if (container == null) return null;

  if (typeof container === 'boolean') {
    return container;
  }

  if (Array.isArray(container)) {
    return container.some(item => hasAiEntitlementToken(item));
  }

  if (typeof container === 'string') {
    return container
      .split(',')
      .map(token => token.trim())
      .some(token => hasAiEntitlementToken(token));
  }

  if (typeof container === 'object') {
    let sawAiKey = false;

    for (const [key, value] of Object.entries(container)) {
      if (hasAiEntitlementToken(key)) {
        sawAiKey = true;
        if (Boolean(value)) {
          return true;
        }
      }
    }

    if (sawAiKey) {
      return false;
    }

    return null;
  }

  return null;
};

const readExplicitAiEntitlement = (...sources) => {
  const explicitFlags = [];

  sources.forEach(source => {
    if (!source || typeof source !== 'object') return;

    AI_ENTITLEMENT_DIRECT_KEYS.forEach(key => {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        explicitFlags.push(Boolean(source[key]));
      }
    });

    AI_ENTITLEMENT_NESTED_KEYS.forEach(key => {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        const parsed = readAiEntitlementFromContainer(source[key]);
        if (parsed !== null) {
          explicitFlags.push(parsed);
        }
      }
    });
  });

  if (!explicitFlags.length) {
    return null;
  }

  return explicitFlags.some(Boolean);
};

const hasCouponIdentifiers = source =>
  isNonEmptyString(source?.couponCode) ||
  isNonEmptyString(source?.couponId) ||
  isNonEmptyString(source?.voucher);

export const hasCorporateVoucher = user => hasCouponIdentifiers(getProjectedProtectedData(user));

const getSubscriptionPeriodEnd = user => {
  const subscriptionData = getPrivateProfileData(user)?.subscriptionData;
  const currentPeriodEnd = Number(subscriptionData?.current_period_end);
  return Number.isFinite(currentPeriodEnd) ? currentPeriodEnd : null;
};

const toSecondsTimestamp = value => {
  if (value == null || value === '') return null;

  if (typeof value === 'number' || /^[0-9]+$/.test(String(value))) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return null;
    return numeric > 9999999999 ? Math.floor(numeric / 1000) : Math.floor(numeric);
  }

  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? Math.floor(timestamp / 1000) : null;
};

const resolveProvider = user => {
  const privateData = getPrivateProfileData(user);
  const publicSubscription = user?.attributes?.profile?.publicData?.subscription;
  const provider = privateData?.subscriptionProvider || publicSubscription?.provider;
  const source = (
    provider ||
    publicSubscription?.source ||
    publicSubscription?.store ||
    publicSubscription?.platform ||
    ''
  )
    .toString()
    .toLowerCase();

  if (
    source.includes('revenuecat') ||
    source.includes('app_store') ||
    source.includes('play_store') ||
    source.includes('ios') ||
    source.includes('android') ||
    publicSubscription?.isActive ||
    (publicSubscription &&
      (Object.prototype.hasOwnProperty.call(publicSubscription, 'isActive') ||
        Boolean(publicSubscription.expiryDate)))
  ) {
    return 'revenuecat_mobile';
  }

  if (privateData?.stripeSubscriptionId || privateData?.subscriptionData) {
    return 'stripe_web';
  }

  return 'unknown';
};

const isPaidInvoice = invoice => {
  if (!invoice) return false;
  return (
    invoice.status === 'paid' || invoice.paid === true || Number(invoice.amount_remaining) === 0
  );
};

export const getSubscriptionAccess = (user, nowSeconds = Math.floor(Date.now() / 1000)) => {
  const emptyAccess = {
    isSubscribed: false,
    isTrialing: false,
    isBillingPeriodActive: false,
    latestInvoicePaid: false,
    shouldUnlockContent: false,
    provider: 'unknown',
    status: 'none',
    manageSource: 'unknown',
    lockReason: 'no_subscription',
  };

  if (!user?.id) return emptyAccess;

  const privateData = getPrivateProfileData(user);
  const publicSubscription = user?.attributes?.profile?.publicData?.subscription;
  const subscriptionData = privateData?.subscriptionData || {};
  const provider = resolveProvider(user);
  const manageSource =
    provider === 'stripe_web' ? 'web' : provider === 'revenuecat_mobile' ? 'mobile' : 'unknown';

  if (hasCorporateVoucher(user) || hasAllowedDomainSubscriptionOverride(user)) {
    return {
      ...emptyAccess,
      isSubscribed: true,
      shouldUnlockContent: true,
      provider,
      status: 'active',
      manageSource,
      lockReason: null,
    };
  }

  if (provider === 'revenuecat_mobile' && publicSubscription) {
    const expirySeconds = toSecondsTimestamp(publicSubscription.expiryDate);
    const active =
      publicSubscription.isActive === true && (!expirySeconds || expirySeconds >= nowSeconds);
    return {
      ...emptyAccess,
      isSubscribed: active,
      isBillingPeriodActive: active,
      latestInvoicePaid: active,
      shouldUnlockContent: active,
      provider,
      status: active ? 'active' : 'expired',
      manageSource,
      lockReason: active ? null : 'mobile_subscription_expired',
    };
  }

  const status = privateData?.stripeSubscriptionStatus || subscriptionData?.status || null;
  const latestInvoice = subscriptionData?.latest_invoice || {};
  const latestInvoiceStatus =
    privateData?.stripeLatestInvoiceStatus || latestInvoice?.status || null;
  const latestInvoicePaid = latestInvoiceStatus === 'paid' || isPaidInvoice(latestInvoice);
  const periodStart = toSecondsTimestamp(subscriptionData?.current_period_start);
  const periodEnd = toSecondsTimestamp(subscriptionData?.current_period_end);
  const trialEnd =
    toSecondsTimestamp(subscriptionData?.trial_end) ||
    toSecondsTimestamp(privateData?.stripeTrialEnd) ||
    toSecondsTimestamp(privateData?.trialExpirationDate);
  const hasSubscription = Boolean(
    privateData?.stripeSubscriptionId ||
      subscriptionData?.id ||
      subscriptionData?.current_period_end
  );
  const periodStarted = periodStart == null || periodStart <= nowSeconds;
  const isBillingPeriodActive = periodEnd != null && periodStarted && periodEnd >= nowSeconds;
  const isTrialing = status === 'trialing' && trialEnd != null && trialEnd >= nowSeconds;
  const ended = Boolean(subscriptionData?.ended_at);
  const canceledNow = Boolean(
    subscriptionData?.canceled_at && !subscriptionData?.cancel_at_period_end
  );
  const cancelAt = toSecondsTimestamp(subscriptionData?.cancel_at);
  const canceledAfterPeriod = cancelAt != null && cancelAt <= nowSeconds;

  if (ended || canceledNow || canceledAfterPeriod) {
    return {
      ...emptyAccess,
      isBillingPeriodActive,
      latestInvoicePaid,
      provider,
      status: 'canceled',
      manageSource,
      lockReason: 'subscription_canceled',
    };
  }

  const activePaid = status === 'active' && latestInvoicePaid && isBillingPeriodActive;
  const shouldUnlockContent = Boolean(activePaid || isTrialing);
  const resolvedStatus = shouldUnlockContent
    ? isTrialing
      ? 'trialing'
      : 'active'
    : !hasSubscription
    ? 'none'
    : !latestInvoicePaid && status !== 'trialing'
    ? 'unpaid'
    : 'expired';

  if (!hasSubscription && hasLegacySubscribedFlag(user)) {
    return {
      ...emptyAccess,
      isSubscribed: true,
      shouldUnlockContent: true,
      provider,
      status: 'active',
      manageSource,
      lockReason: null,
    };
  }

  return {
    isSubscribed: shouldUnlockContent && !isTrialing,
    isTrialing,
    isBillingPeriodActive,
    latestInvoicePaid,
    shouldUnlockContent,
    provider,
    status: resolvedStatus,
    manageSource,
    lockReason: shouldUnlockContent
      ? null
      : resolvedStatus === 'none'
      ? 'no_subscription'
      : resolvedStatus === 'unpaid'
      ? 'invoice_unpaid'
      : isTrialing
      ? null
      : 'subscription_expired',
  };
};

export const buildSubscriptionAccessProfileUpdate = user => {
  const access = getSubscriptionAccess(user);
  const protectedData = getProtectedProfileData(user);
  const privateData = getPrivateProfileData(user);
  const nextSubscribed = access.shouldUnlockContent;
  const nextPrivateData = {
    hasSubscribed: nextSubscribed,
    subscriptionAccessStatus: access.status,
    subscriptionLastCheckedAt: new Date().toISOString(),
    subscriptionProvider: access.provider,
    subscriptionLockReason: access.lockReason || null,
  };
  const shouldUpdate =
    privateData?.hasSubscribed !== nextSubscribed ||
    protectedData?.isSubscribed !== nextSubscribed ||
    privateData?.subscriptionAccessStatus !== access.status ||
    privateData?.subscriptionProvider !== access.provider ||
    (privateData?.subscriptionLockReason || null) !== (access.lockReason || null);

  return shouldUpdate
    ? {
        shouldUpdate: true,
        protectedData: { isSubscribed: nextSubscribed },
        privateData: nextPrivateData,
        access,
      }
    : { shouldUpdate: false, access };
};

const hasActiveStripeSubscription = user => {
  const currentPeriodEnd = getSubscriptionPeriodEnd(user);
  if (currentPeriodEnd == null) {
    return false;
  }

  const now = Math.floor(Date.now() / 1000);
  return currentPeriodEnd > now;
};

const hasLegacySubscribedFlag = user => {
  const protectedData = getProtectedProfileData(user);
  const privateData = getPrivateProfileData(user);
  return Boolean(protectedData?.hasSubscribed || privateData?.hasSubscribed);
};

const hasAllowedDomainSubscriptionOverride = user => {
  const allowedDomains = ['ppcgroup', 'dei', 'vivianlab', 'yopmail', 'gmail'];
  const allowedIDs = ['80976471', '80976472', '80976473', '80976474', '80976475', '80976476'];

  const email = user?.attributes?.email || '';
  const id = getProtectedProfileData(user)?.id || '';

  return allowedDomains.some(domain => email?.includes(domain)) && allowedIDs?.includes(id);
};

const getTrialExpirationTimestamp = user => {
  const trialExpirationDate = getPrivateProfileData(user)?.trialExpirationDate;
  if (!trialExpirationDate) {
    return null;
  }

  const timestamp = new Date(trialExpirationDate).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const isCorporateCustomer = user => {
  return hasCorporateVoucher(user);
};

export const isFreeTrialCustomer = user => {
  const privateData = getPrivateProfileData(user);
  return privateData?.hasTrialStarted;
};

export const getInvoiceUserData = user => {
  return {
    name: user?.attributes?.profile?.displayName,
  };
};

export const getMeetingTitle = tx => {
  const providerName = getDisplayName(tx?.provider);
  const customerName = getDisplayName(tx?.customer);

  return `Meeting | ${providerName} | ${customerName}`;
};

export const renameObjKeys = (obj, newKeys) => {
  const keyValues = Object.keys(obj)?.map(key => {
    const newKey = newKeys[key] || key;
    return { [newKey]: obj[key] };
  });
  return Object.assign({}, ...keyValues);
};

function calculateInvoiceSummary(lineItems) {
  let subtotal = 0;
  let stripeFee = 0;

  // Calculate subtotal and stripe fee
  for (const item of lineItems) {
    if (item.code === 'line-item/hour') {
      subtotal += item.unitPrice.amount;
    } else if (item.code === 'line-item/stripe-fee') {
      stripeFee += item.lineTotal.amount;
    }
  }

  // Calculate total
  const total = subtotal + stripeFee;

  return {
    subtotal: subtotal / 100,
    stripeFee: stripeFee / 100,
    total: total / 100,
    vivianLabFee: 0,
  };
}

export const getReceiptData = tx => {
  const customer = tx?.customer;
  const provider = tx?.provider;
  const booking = tx?.booking;
  const protectedData = tx?.attributes?.protectedData;
  const duration = protectedData?.duration;
  const validLineItems =
    isArrayLength(tx?.attributes?.lineItems) && tx?.attributes?.lineItems?.filter(l => !l.reversal);

  const lineItems = calculateInvoiceSummary(validLineItems);

  const bookingOption = DEFAULT_BOOKING_OPTIONS.find(e => e.value === protectedData?.bookingType);
  const defaultLocale = (typeof window !== 'undefined' && localStorage.getItem('locale')) || 'de';

  return {
    customerName: getDisplayName(customer),
    providerName: getDisplayName(provider),
    date: moment(booking?.attributes?.start).format('MM/DD/YYYY hh:mm:ss'),
    type: defaultLocale === 'en' ? bookingOption?.label : bookingOption?.greekLabel,
    duration: defaultLocale === 'en' ? `${duration} minutes` : `${duration} λεπτά`,
    lineItems,
  };
};

export const getSavedReceiptData = tx => {
  return tx?.id && tx?.attributes?.metadata?.receiptData;
};

export const getTotalPayoutForDoctors = txs => {
  const allPrices = isArrayLength(txs)
    ? txs
        .filter(t => !t.attributes?.metadata?.isUnpaid)
        .map(tx => parseFloat(tx?.attributes?.protectedData?.service?.price))
        .reduce((acc, curr) => acc + curr, 0)
    : null;

  const totalPrice = allPrices?.toFixed(2);
  return totalPrice || 0;
};

export const getWalletBalance = user => {
  return user?.attributes?.profile?.protectedData?.walletAmount || 0;
};

export const getTutorialsData = t => {
  const id = t?.id;
  const title = t?.attributes?.title || '';
  const description = t?.attributes?.description || '';
  const doctorName = t?.attributes?.doctorName || '';
  const videoURL = t?.attributes?.videoURL || '';
  const rawUrl = t?.attributes?.vimeoLink || '';
  const tagEnglish = t?.attributes?.tagEnglish || [];
  const tagGreek = t?.attributes?.tagGreek || [];
  const rank = t?.attributes?.rank || 0;
  const slug = t?.attributes?.slug || 0;
  const createdAt = t?.attributes?.createdAt || null;
  const doctor = t?.attributes?.doctor || null;
  const transcript = t?.attributes?.transcript || null;
  const symptom_filter = t?.attributes?.symptom_filter || null;
  const thumbnail = t?.attributes?.thumbnail?.data?.attributes?.url;
  const moduleSlug = isArrayLength(t?.attributes?.tutorials_subcategories?.data)
    ? t?.attributes?.tutorials_subcategories?.data[0]?.attributes?.slug
    : null;
  const vimeoURL = cleanVimeoUrl(rawUrl);

  return {
    id,
    title,
    doctorName,
    description,
    createdAt,
    thumbnail,
    videoURL,
    vimeoURL,
    symptom_filter,
    tagEnglish,
    tagGreek,
    rank,
    doctor,
    slug,
    transcript,
    moduleSlug,
  };
};

export const getUserFullName = user => {
  if (!user.id) return null;
  return user?.attributes?.profile?.publicData?.fullName || user?.attributes?.profile?.displayName;
};

export const getUserId = user => user?.id?.uuid || null;

export const isPartialPayment = tx => {
  return tx?.attributes?.metadata?.isPartialPayment;
};

export function extractYouTubeVideoId(url) {
  const regexes = [
    /https?:\/\/youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /https?:\/\/(?:www\.)?youtube\.com\/(?:.*\?v=|.*\/)([a-zA-Z0-9_-]{11})/,
  ];

  for (const regex of regexes) {
    const match = url.match(regex);
    if (match) return match[1];
  }

  return null;
}

export const getOwnVoucher = user => {
  return user?.attributes?.profile?.protectedData?.voucher;
};
export const getListIdBasedOnUserType = params => {
  const { userType, couponCode, isContentVoucher } = params || {};

  // Determine the list ID based on the conditions
  if (userType === 'individual') {
    if (
      couponCode &&
      (couponCode.toLowerCase().includes('bay') ||
        couponCode.toLowerCase().includes('jde') ||
        couponCode.toLowerCase().includes('wella') ||
        couponCode.toLowerCase().includes('cchellenic') ||
        couponCode.toLowerCase().includes('winner'))
    ) {
      return SENDGRID_CONTRACT_LIST.BAYER_AND_JDE;
    }

    if (!isContentVoucher) {
      return SENDGRID_CONTRACT_LIST.FREE_TRIAL;
    }
  }
};

export const hasValidSubscription = user => {
  if (!user?.id) return false;
  return getSubscriptionAccess(user).shouldUnlockContent;
};

export const hasActiveSubscriptionAccess = user => {
  if (!user?.id) return false;

  return (
    hasActiveStripeSubscription(user) ||
    hasAllowedDomainSubscriptionOverride(user) ||
    hasLegacySubscribedFlag(user)
  );
};

export const hasActiveTrialAccess = user => {
  if (!user?.id) return false;

  const privateData = getPrivateProfileData(user);
  if (!privateData?.hasTrialStarted) {
    return false;
  }

  const trialExpirationTimestamp = getTrialExpirationTimestamp(user);
  return trialExpirationTimestamp == null ? true : trialExpirationTimestamp > Date.now();
};

export const hasValidCouponAccess = user => {
  if (!user?.id) return false;

  if (hasCorporateVoucher(user)) {
    return true;
  }

  const privateData = getPrivateProfileData(user);
  const hasCouponIdentity = isCorporateUser(user);

  if (!hasCouponIdentity) {
    return false;
  }

  if (hasActiveStripeSubscription(user)) {
    return true;
  }

  const trialExpirationTimestamp = getTrialExpirationTimestamp(user);
  if (trialExpirationTimestamp != null) {
    return trialExpirationTimestamp > Date.now();
  }

  return Boolean(privateData?.hasTrialStarted || hasLegacySubscribedFlag(user));
};

export const isCorporateUser = (user, couponState = {}) => {
  if (!user?.id) return false;

  const protectedData = getProtectedProfileData(user);
  const privateData = getPrivateProfileData(user);
  const couponStateType = normalizeEntitlementToken(couponState?.subscriptionType);

  return (
    privateData?.isCorporateUser === true ||
    protectedData?.subscriptionType === 'corporate' ||
    couponStateType === 'corporate' ||
    couponState?.isCorporateUser === true ||
    isCorporateCustomer(user) ||
    hasCouponIdentifiers(couponState)
  );
};

export const hasCouponAiAccess = (user, couponState = {}) => {
  if (!user?.id) return false;

  const protectedData = getProtectedProfileData(user);
  const privateData = getPrivateProfileData(user);

  const explicitAiEntitlement = readExplicitAiEntitlement(protectedData, privateData, couponState);
  if (explicitAiEntitlement !== null) {
    return explicitAiEntitlement;
  }

  const hasCouponIdentity = isCorporateCustomer(user) || hasCouponIdentifiers(couponState);
  if (!hasCouponIdentity) {
    return false;
  }

  if (hasActiveStripeSubscription(user)) {
    return true;
  }

  const trialExpirationTimestamp = getTrialExpirationTimestamp(user);
  if (trialExpirationTimestamp != null) {
    return trialExpirationTimestamp > Date.now();
  }

  return Boolean(privateData?.hasTrialStarted || hasLegacySubscribedFlag(user));
};

export const canAccessAI = (user, couponState = {}) => {
  if (!user?.id) return false;

  if (hasCorporateVoucher(user) || hasCouponIdentifiers(couponState)) {
    return true;
  }

  if (hasValidSubscription(user)) {
    return true;
  }

  return isCorporateUser(user, couponState) && hasCouponAiAccess(user, couponState);
};

export const hasPremiumContentAccess = user => {
  if (!user?.id) return false;

  if (hasCorporateVoucher(user)) {
    return true;
  }

  return getSubscriptionAccess(user).shouldUnlockContent || hasValidCouponAccess(user);
};

export const shouldLockAccess = currentUser => {
  return !hasPremiumContentAccess(currentUser);
};

export const AI_PAYWALL_FEATURE_KEY = 'ai_box';
export const DEFAULT_AI_FREE_QUESTIONS_LIMIT = 3;

export const getAIFreeQuestionsLimit = limit => {
  const parsedLimit = Number(limit);
  return Number.isFinite(parsedLimit) && parsedLimit > 0
    ? parsedLimit
    : DEFAULT_AI_FREE_QUESTIONS_LIMIT;
};

export const countAIQuestionsAsked = conversations => {
  if (!Array.isArray(conversations)) return 0;

  let total = 0;
  conversations.forEach(chat => {
    const messages =
      chat && typeof chat !== 'string' && Array.isArray(chat.messages) ? chat.messages : [];

    messages.forEach(message => {
      if (message && typeof message.question === 'string' && message.question.trim()) {
        total += 1;
      }
    });
  });

  return total;
};

export const hasRemainingAIFreeQuestions = ({
  conversations,
  freeQuestionsLimit = DEFAULT_AI_FREE_QUESTIONS_LIMIT,
}) => {
  const normalizedLimit = getAIFreeQuestionsLimit(freeQuestionsLimit);
  const totalQuestionsAsked = countAIQuestionsAsked(conversations);
  return totalQuestionsAsked < normalizedLimit;
};

export const shouldBlockWithPaywall = ({
  featureKey,
  user,
  conversations = [],
  freeQuestionsLimit = DEFAULT_AI_FREE_QUESTIONS_LIMIT,
}) => {
  const isLockedUser = shouldLockAccess(user);
  if (!isLockedUser) {
    return false;
  }

  if (featureKey === AI_PAYWALL_FEATURE_KEY) {
    return !hasRemainingAIFreeQuestions({ conversations, freeQuestionsLimit });
  }

  return true;
};

export const shouldPreventJoinMeeting = ({ user, eventAttributes }) => {
  const isSubscribed = hasValidSubscription(user);
  const isCorporate = isCorporateCustomer(user);
  const isFreeTrial = isFreeTrialCustomer(user);
  const isGroupEvent = eventAttributes?.isGroup;

  // Corporate and Subscribed users can access all events
  if (isCorporate || isSubscribed) return false;

  // Free trial users can only access group events
  if (isFreeTrial && isGroupEvent) return false;

  // Unsubscribed, non-corporate users can also only access group events
  if (!isSubscribed && !isCorporate && isGroupEvent) return false;

  // Otherwise, prevent access
  return true;
};
